import { cn } from "@eisox/design-system";
import { BinIcon, CrossIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import type { PeriodType } from "../helpers/schemas.helper";
import { Period } from "./period";

export interface UpdatePeriodProps {
  period: PeriodType;
  onPeriodChange?: (period: PeriodType) => void;
  removed?: boolean;
  onRemovedChange?: (removed: boolean) => void;
  error?: string;
}

export const UpdatePeriod: React.FC<UpdatePeriodProps> = ({
  period: periodProp,
  onPeriodChange,
  removed: removedProp,
  onRemovedChange,
  error,
}) => {
  const [period, setPeriod] = useControllableState({
    prop: periodProp,
    onChange: onPeriodChange,
    defaultProp: { begin: "", end: "" },
  });
  const [removed, setRemoved] = useControllableState({
    prop: removedProp,
    onChange: onRemovedChange,
    defaultProp: false,
  });

  const handleBeginChange = (begin?: string) => setPeriod({ ...period, begin: begin ?? "" });
  const handleEndChange = (end?: string) => setPeriod({ ...period, end: end ?? "" });

  const toggleRemoved = () => setRemoved(prev => !prev);

  return (
    <div className={cn("relative grid w-full flex-col gap-[5px] text-base")} data-testid="update-period">
      <div className={cn("grid flex-1 grid-cols-[1fr_19px] items-center gap-[10px]")}>
        <Period
          begin={period.begin}
          end={period.end}
          onBeginChange={handleBeginChange}
          onEndChange={handleEndChange}
          disabled={removed}
        />
        {removed ? (
          <CrossIcon
            className={cn("cursor-pointer text-primary-dark-blue")}
            role="button"
            aria-label="cross"
            onClick={toggleRemoved}
          />
        ) : (
          <BinIcon
            className={cn("h-[19px] w-[19px] cursor-pointer text-primary-dark-blue")}
            role="button"
            aria-label="bin"
            onClick={toggleRemoved}
          />
        )}
        {removed && (
          <span
            className={cn("absolute top-1/2 left-[-3px] h-[1px] w-[calc(100%-24px)] bg-primary-dark-gray")}
            data-testid="strikethrough"
          />
        )}
      </div>
      {error && <p className={cn("text-primary-red")}>{error}</p>}
    </div>
  );
};

UpdatePeriod.displayName = "UpdatePeriod";
